import { create } from "zustand";
import { persist } from "zustand/middleware";
import axios from "axios";

/**
 * Zustand store for managing the selected main location (studio).
 * Persists the selected studio ID to localStorage so the user's choice is remembered.
 */
export const useLocationStore = create(
  persist(
    (set, get) => ({
      mainLocations: [],
      selectedLocationId: null,
      studioDialogDisabled: false,
      forceDialogOpen: false,
      loading: false,
      error: null,

      /**
       * Fetch main locations from the API.
       * Called once on app mount.
       */
      fetchMainLocations: async () => {
        const base = process.env.REACT_APP_BACKEND_ADDRESS;
        set({ loading: true, error: null });
        try {
          const response = await axios.get(
            `${base}/lesson-type-locations/main-locations`
          );
          const locations = response.data || [];
          const currentId = get().selectedLocationId;

          // If no location is selected yet, or the previously selected one
          // is no longer in the list, auto-select the first one.
          const validSelection = locations.some((l) => l.id === currentId);
          const selectedId =
            validSelection && currentId !== null
              ? currentId
              : locations.length > 0
                ? locations[0].id
                : null;

          // If the persisted selection was stale (no longer exists),
          // re-enable the studio dialog so the user is prompted to choose.
          const wasReset = currentId !== null && !validSelection && locations.length > 0;

          set({
            mainLocations: locations,
            selectedLocationId: selectedId,
            loading: false,
            ...(wasReset ? { studioDialogDisabled: false } : {}),
          });
        } catch (err) {
          console.error("Failed to fetch main locations:", err);
          // On error, clear stale selectedLocationId if no locations are loaded
          // to prevent downstream components from using an invalid ID.
          const { mainLocations: existing } = get();
          set({
            loading: false,
            error: err,
            ...(existing.length === 0 ? { selectedLocationId: null } : {}),
          });
        }
      },

      /**
       * Set the selected location by ID.
       */
      setSelectedLocationId: (id) => set({ selectedLocationId: id }),

      /**
       * Set whether the studio selection dialog should be disabled on startup.
       */
      setStudioDialogDisabled: (value) =>
        set({ studioDialogDisabled: value }),

      /**
       * Force the studio dialog open (e.g. from "Zmień" link).
       */
      openStudioDialog: () => set({ forceDialogOpen: true }),
      closeStudioDialog: () => set({ forceDialogOpen: false }),

      /**
       * Derived getter: returns the full object of the currently selected location.
       */
      getSelectedLocation: () => {
        const { mainLocations, selectedLocationId } = get();
        return mainLocations.find((l) => l.id === selectedLocationId) || null;
      },
    }),
    {
      name: "location-storage",
      partialize: (state) => ({
        selectedLocationId: state.selectedLocationId,
        studioDialogDisabled: state.studioDialogDisabled,
      }),
    }
  )
);
